import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectionState } from '../../types';

const initialState: SelectionState = {
  selectedIds: [],
  hoveredId: null,
  selectionBox: null,
};

const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    /**
     * Set selection to a single element.
     * @param state - Selection state
     * @param action - Payload is element ID
     */
    selectElement: (state, action: PayloadAction<string>) => {
      state.selectedIds = [action.payload];
    },

    /**
     * Set selection to a list of elements, replacing current selection.
     * @param state - Selection state
     * @param action - Payload is array of element IDs
     */
    selectElements: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload;
    },

    /**
     * Add an element to current selection (multi-select).
     * @param state - Selection state
     * @param action - Payload is element ID to add
     */
    addToSelection: (state, action: PayloadAction<string>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },

    /**
     * Remove an element from current selection.
     * @param state - Selection state
     * @param action - Payload is element ID to remove
     */
    removeFromSelection: (state, action: PayloadAction<string>) => {
      state.selectedIds = state.selectedIds.filter((id) => id !== action.payload);
    },

    /**
     * Toggle element's selection state (add if not selected, remove if selected).
     * @param state - Selection state
     * @param action - Payload is element ID to toggle
     */
    toggleSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((eId) => eId !== id);
      } else {
        state.selectedIds.push(id);
      }
    },

    /**
     * Clear all selections and selection box.
     */
    clearSelection: (state) => {
      state.selectedIds = [];
      state.selectionBox = null;
    },

    /**
     * Set the currently hovered element.
     * @param state - Selection state
     * @param action - Payload is element ID or null
     */
    setHovered: (state, action: PayloadAction<string | null>) => {
      state.hoveredId = action.payload;
    },

    /**
     * Set the selection box for drag-select operations.
     * @param state - Selection state
     * @param action - Payload is box coordinates or null to clear
     */
    setSelectionBox: (
      state,
      action: PayloadAction<{
        startX: number;
        startY: number;
        endX: number;
        endY: number;
      } | null>
    ) => {
      state.selectionBox = action.payload;
    },

    /**
     * Select all elements from a provided list.
     * @param state - Selection state
     * @param action - Payload is array of all element IDs to select
     */
    selectAll: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload;
    },
  },
});

export const {
  selectElement,
  selectElements,
  addToSelection,
  removeFromSelection,
  toggleSelection,
  clearSelection,
  setHovered,
  setSelectionBox,
  selectAll,
} = selectionSlice.actions;

export default selectionSlice.reducer;
